import { useMemo } from 'react'

import { Source, Layer, useMap } from 'react-map-gl/mapbox'

import { duration } from '@/insights/common/formatUtils'
import { GeofencesInView } from '@/lib/map/GeofencesInView'

import { useMapContext } from './MapContext'
import { Severity } from './types'
import { levelToSeverity, severityToLevel } from './utils'
// import { startRepaintLoop } from './utils'

export const MapLayers = () => {
  const context = useMapContext()
  const { companyId } = context.filters
  const { current: map } = useMap()
  // Need to do this so we don't blank out the current asset while we're waiting for the full data fetch
  const tempCurrentAsset = useMemo(() => {
    if (!context.currentAssetId) {
      return undefined
    }
    const data = context.filteredAssets.find((d) => d.id === context.currentAssetId)
    if (!data) {
      return undefined
    }
    const asset = {
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [data.location.longitude, data.location.latitude],
      },
      properties: {
        id: data.id,
        name: data.name,
        highestSeverity:
          context.mapState.severitySource === 'alarms' ? data.highestSeverity : data.highestAlertSeverity,
      },
    }
    return asset
  }, [context.mapState.severitySource, context.currentAssetId, context.filteredAssets])

  const currentAsset = useMemo(() => {
    const assetData = context.currentAssetData
    const animatedLocation = context.animatedAssetLocation
    let asset = undefined
    if (assetData && animatedLocation) {
      if (assetData.id !== context.currentAssetId) {
        return undefined
      }
      const highestSeverity = levelToSeverity(
        assetData.activeAlarms
          .map((a) => severityToLevel(a.severity as Severity))
          .sort()
          .reverse()[0]
      )
      asset = {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [animatedLocation.longitude, animatedLocation.latitude],
        },
        properties: {
          id: assetData.id,
          highestSeverity,
          name: assetData.gatewayOn ? assetData.name : `${assetData.name}\n(${duration(assetData.umdSampleTime)})`,
          gatewayOn: assetData.gatewayOn,
          powerOn: assetData.powerOn,
        },
      }
    }
    map?.triggerRepaint()
    return asset
  }, [context.currentAssetId, context.currentAssetData, context.animatedAssetLocation])
  /*
  const state =
    map && context.currentAssetId
      ? map.getFeatureState({
          id: context.currentAssetId,
          source: 'assets',
        })
      : 'no asset selected'
  */

  return (
    <>
      {/* Nothing until the user picks zOrders or categories in the geofence selection. */}
      <GeofencesInView
        companyId={companyId}
        zOrders={context.mapState.zOrders || []}
        categoryIds={context.mapState.geofenceCategoryIds || []}
        requireFilter
      />
      <Source
        key={`source-${context.mapState.style}-${context.mapState.clustersEnabled}-${companyId || 0}`}
        id="assets-source"
        type="geojson"
        data={context.geojsonData}
        cluster={context.mapState.clustersEnabled}
        clusterMaxZoom={14}
        clusterRadius={50}
        clusterProperties={{
          maxSeverity: ['max', ['get', 'severityValue']],
        }}
      >
        {/* Cluster circles - only show when clustered AND not showing individual markers */}
        <Layer
          id="clustered-asset-circle"
          type="circle"
          source="assets"
          filter={['has', 'point_count']}
          layout={{
            visibility: context.mapState.focus ? 'none' : 'visible',
          }}
          paint={{
            'circle-color': [
              'step',
              ['get', 'maxSeverity'],
              '#3b82f6', // default blue (no alarms)
              0,
              '#22c55e', // informational
              1,
              '#eab308', // warning
              2,
              '#ef4444', // critical
            ],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              20, // base size
              100,
              25, // 100+ points = 25px
              750,
              30, // 750+ points = 30px
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
          }}
        />
        {/* Cluster count labels - only show when clustered AND not showing individual markers */}
        <Layer
          id="clustered-asset-label"
          source="assets"
          type="symbol"
          filter={['has', 'point_count']}
          layout={{
            visibility: context.mapState.focus ? 'none' : 'visible',
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-allow-overlap': true,
          }}
          paint={{
            'text-color': '#fff',
          }}
        />
        <Layer
          id="unclustered-asset-marker"
          source="assets"
          type="symbol"
          filter={['!', ['has', 'point_count']]}
          layout={{
            visibility: context.mapState.focus ? 'none' : 'visible',
            'icon-image': [
              'case',
              ['==', ['get', 'gatewayOn'], false],
              'offline-marker',
              ['==', ['get', 'highestSeverity'], 'informational'],
              'info-marker',
              ['==', ['get', 'highestSeverity'], 'warning'],
              'warning-marker',
              ['==', ['get', 'highestSeverity'], 'critical'],
              'critical-marker',
              'default-marker',
            ],
            'icon-allow-overlap': true,
          }}
          paint={{
            'icon-opacity': ['case', ['==', ['feature-state', 'selected'], true], 0.0, 1],
          }}
        />
        {context.mapState.assetLabels && (
          <Layer
            id="unclustered-asset-label"
            source="assets"
            type="symbol"
            filter={['!', ['has', 'point_count']]}
            layout={{
              visibility: context.mapState.focus ? 'none' : 'visible',
              'text-field': ['get', 'name'],
              'text-font': ['Inter Medium'],
              'text-size': 13,
              'text-anchor': 'top',
              'text-offset': [0, 1.9],
              'icon-image': [
                'case',
                ['==', ['get', 'selected'], true],
                context.mapState.style === 'satellite' ? 'label-bg-dark-selected' : 'label-bg-light-selected',
                context.mapState.style === 'satellite' ? 'label-bg-dark' : 'label-bg-light',
              ],
              'icon-text-fit': 'both',
              'icon-allow-overlap': true,
            }}
            paint={{
              'text-color': context.mapState.style === 'satellite' ? 'white' : '#0f172a',
            }}
          />
        )}
      </Source>
      {(currentAsset || tempCurrentAsset) && (
        <Source
          key={`source-${context.mapState.style}-${context.mapState.clustersEnabled}-${context.currentAssetId}`}
          id="current-asset-source"
          type="geojson"
          data={currentAsset || tempCurrentAsset}
          cluster={false}
        >
          <Layer
            id="current-asset-marker"
            source="currentAsset"
            type="symbol"
            layout={{
              'icon-image': [
                'case',
                ['==', ['get', 'gatewayOn'], false],
                // 'pulsing-dot',
                'animated-offline-marker',
                ['==', ['get', 'highestSeverity'], 'informational'],
                // 'pulsing-dot',
                'animated-info-marker',
                ['==', ['get', 'highestSeverity'], 'warning'],
                // 'pulsing-dot',
                'animated-warning-marker',
                ['==', ['get', 'highestSeverity'], 'critical'],
                // 'pulsing-dot',
                'animated-critical-marker',
                // 'pulsing-dot',
                'animated-default-marker',
              ],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-size': 1.2,
              'icon-offset': [0, 0],
            }}
          />
        </Source>
      )}
    </>
  )
}
